import type { BadgeTone } from '@/shared/components/ui/Badge';

export const SECTION3_CONTACT_CHANNELS = ['call', 'whatsapp', 'email'] as const;
export const SECTION3_QUALIFIED_REASONS = [
  'discovery-complete',
  'budget-confirmed',
  'authority-confirmed',
  'need-confirmed',
  'timeline-confirmed',
] as const;
export const SECTION3_NOT_QUALIFIED_REASONS = [
  'no-budget',
  'no-authority',
  'no-need',
  'bad-timing',
  'duplicate',
  'invalid-contact',
] as const;

export type Section3Status =
  | 'success'
  | 'pending-routing'
  | 'pending-nurture'
  | 'pending-manual'
  | 'pending-approval'
  | 'retryable'
  | 'dead-letter'
  | 'forbidden'
  | 'unauthenticated'
  | 'not-found'
  | 'conflict'
  | 'validation-error'
  | 'error';

export function section3StatusTone(status: Section3Status): BadgeTone {
  switch (status) {
    case 'success': return 'success';
    case 'pending-routing':
    case 'pending-nurture':
    case 'pending-approval':
    case 'retryable': return 'warning';
    case 'pending-manual':
    case 'dead-letter':
    case 'conflict':
    case 'forbidden':
    case 'unauthenticated':
    case 'not-found':
    case 'validation-error':
    case 'error': return 'danger';
    default: return 'neutral';
  }
}

export function section3StatusLabel(status: Section3Status): string {
  switch (status) {
    case 'success': return 'Committed';
    case 'pending-routing': return 'Pending routing';
    case 'pending-nurture': return 'Pending nurture';
    case 'pending-manual': return 'Needs manual recovery';
    case 'pending-approval': return 'Pending approval';
    case 'retryable': return 'Retryable';
    case 'dead-letter': return 'Dead letter';
    case 'forbidden': return 'Forbidden';
    case 'unauthenticated': return 'Sign in required';
    case 'not-found': return 'Not found';
    case 'conflict': return 'Conflict';
    case 'validation-error': return 'Invalid input';
    default: return 'Error';
  }
}

export function section3StatusFromError(error: unknown): Section3Status {
  if (!error || typeof error !== 'object' || !('isAxiosError' in error)) return 'error';
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 409) return 'conflict';
  if (status === 400) return 'validation-error';
  if (status != null && status >= 500) return 'retryable';
  return 'error';
}
